import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCompanyLogoSrc } from '../utils/mediaUrl';

const API_BASE = 'https://api.insightos.co.uk';

function formatCurrency(value) {
  const num = Number(value || 0);
  return `£${num.toFixed(2)}`;
}

function getPaymentProvider(link = '') {
  const normalized = link.toString().toLowerCase();
  if (normalized.includes('paypal')) {
    return { name: 'PayPal', badgeClass: 'bg-[#003087] text-white' };
  }
  if (normalized.includes('stripe')) {
    return { name: 'Stripe', badgeClass: 'bg-[#635bff] text-white' };
  }
  if (normalized.includes('square')) {
    return { name: 'Square', badgeClass: 'bg-[#111827] text-white' };
  }
  return null;
}

export default function CustomerInvoice() {
  const { id } = useParams();
  const [email, setEmail] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const companyLogoSrc = getCompanyLogoSrc(invoice?.companyData || {});

  const totals = useMemo(() => {
    if (!invoice) return null;
    const materialsTotal = (invoice.materials || []).reduce((sum, m) => sum + (Number(m.price) || 0), 0);
    const baseTotal = Number(invoice.projectPrice || 0) + materialsTotal;
    const subtotal = invoice.isVatDeductible ? baseTotal * 0.8 : baseTotal;
    const vat = subtotal * (Number(invoice.vatRate || 0) / 100);
    const total = subtotal + vat;
    return { materialsTotal, subtotal, vat, total };
  }, [invoice]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInvoice(null);

    try {
      const res = await fetch(`${API_BASE}/api/invoice/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || 'Unable to load invoice');
      }
      setInvoice(data.invoice);
    } catch (err) {
      setError(err?.message || 'Unable to load invoice');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-slate-50 px-4 py-10"
      style={{ backgroundImage: "url('/background.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="mx-auto w-full max-w-3xl">
        <div className="rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
          <div className="text-xl font-semibold text-slate-900">View Invoice</div>
          <p className="mt-1 text-sm text-slate-600">Enter the email address tied to this invoice.</p>

          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="text-xs font-semibold text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="you@company.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {loading ? 'Loading…' : 'View Invoice'}
            </button>
          </form>

          {error ? (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}
        </div>

        {invoice && (
          <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-2xl font-semibold text-slate-900">INVOICE</div>
                <div className="text-sm text-slate-600">{invoice.companyData?.CompanyName || 'Company'}</div>
                {invoice.companyData?.CompanyNumber ? (
                  <div className="text-xs text-slate-500">Company No: {invoice.companyData.CompanyNumber}</div>
                ) : null}
              </div>
              {companyLogoSrc ? (
                <img
                  src={companyLogoSrc}
                  alt="Company Logo"
                  className="h-14 w-auto object-contain"
                />
              ) : null}
            </div>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold text-slate-500">INVOICE FOR</div>
              <div className="text-lg font-semibold text-slate-900">{invoice.projectName}</div>
              {invoice.clientName ? (
                <div className="text-sm text-slate-600">Client: {invoice.clientName}</div>
              ) : null}
              {invoice.customerVatNumber ? (
                <div className="text-xs text-slate-500">VAT Number: {invoice.customerVatNumber}</div>
              ) : null}
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Project Price</span>
                <span className="font-semibold text-slate-900">{formatCurrency(invoice.projectPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Labor Hours</span>
                <span className="font-semibold text-slate-900">{Number(invoice.laborHours || 0).toFixed(2)} hrs</span>
              </div>

              {(invoice.materials || []).length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-xs font-semibold text-slate-600 mb-2">Materials</div>
                  {invoice.materials.map((mat, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-600">{mat.description || `Material ${idx + 1}`}</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(mat.price)}</span>
                    </div>
                  ))}
                  <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 font-semibold">
                    <span className="text-slate-700">Materials Total</span>
                    <span className="text-slate-900">{formatCurrency(totals?.materialsTotal)}</span>
                  </div>
                </div>
              )}

              {invoice.isVatDeductible && totals ? (
                <div className="flex justify-between text-red-600">
                  <span>VAT Deductible (20%)</span>
                  <span className="font-semibold">- {formatCurrency((Number(invoice.projectPrice || 0) + (totals.materialsTotal || 0)) * 0.2)}</span>
                </div>
              ) : null}

              {totals && (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-semibold text-slate-900">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">VAT ({invoice.vatRate}%)</span>
                    <span className="font-semibold text-slate-900">{formatCurrency(totals.vat)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-3">
                    <span>Total</span>
                    <span>{formatCurrency(totals.total)}</span>
                  </div>
                </>
              )}

              {invoice.paymentLink ? (
                <div className="mt-4 flex items-center justify-between rounded-lg bg-blue-50 p-3">
                  {getPaymentProvider(invoice.paymentLink) ? (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getPaymentProvider(invoice.paymentLink).badgeClass}`}>
                      {getPaymentProvider(invoice.paymentLink).name}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-blue-900">Payment</span>
                  )}
                  <a
                    href={invoice.paymentLink}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500"
                  >
                    Pay Now
                  </a>
                </div>
              ) : null}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => window.print()}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                Print Invoice
              </button>
            </div>
          </div>
        )}

        <div className="mt-10 rounded-2xl bg-white/95 p-6 shadow-sm border border-slate-200">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <img src="/1.png" alt="InsightOS" className="h-10 w-auto object-contain" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Try InsightOS free today</div>
                <a
                  href="https://insightos.co.uk"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-700 hover:underline"
                >
                  https://insightos.co.uk
                </a>
              </div>
            </div>
            <a
              href="https://insightos.co.uk"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              Start Free Trial
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
